import { type CreateIsoMeasureDto, category2 } from '@/src/api/types/generated-types'
import { ChevronsDoubleUp, ChevronsTripleUp, ChevronsUp } from '@/src/assets/icons'
import { ActionModal } from '@/src/components/ActionModal'
import { FormDatePicker } from '@/src/components/Form/FormDatePicker'
import { FormInput } from '@/src/components/Form/FormInput'
import { FormSelect } from '@/src/components/Form/FormSelect'
import { FormTextarea } from '@/src/components/Form/FormTextarea'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { useCreateMeasureFromInsight } from '@/src/features/insight-inbox/hooks/useCreateMeasureFromInsight.hook'
import { useEnergyGoals } from '@/src/features/iso-suite/energy-goals/hooks/useEnergyGoals.hook'
import { useCreateMeasure } from '@/src/features/iso-suite/measures/hooks/useCreateMeasure.hook'
import { useIsIso50001ModuleEnabled } from '@/src/hooks/location-module/location-module.hooks'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { CLEAR_OPTION_VALUE } from '@/src/utils/constants'
import { Text } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import { useUsers } from '@hooks/useUsers.hook'
import { getEndOfYear } from '@utils/dateUtil'
import moment from 'moment'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

const PrioritySelectOption: FC<{ priority: 'LOW' | 'MEDIUM' | 'HIGH' }> = ({ priority }) => {
  const { t } = useTranslation()
  const icons = {
    ['LOW']: <ChevronsUp />,
    ['MEDIUM']: <ChevronsDoubleUp />,
    ['HIGH']: <ChevronsTripleUp />,
  }

  return (
    <FlexRow borderRadius={'6px'} marginLeft={'-5px'} alignItems={'center'} gap={'3px'}>
      {icons[priority]}
      <Text fontWeight={400} color={'#3C3C3C'}>
        {t(`iso_measures_priority_${priority.toLowerCase()}`)}
      </Text>
    </FlexRow>
  )
}

export const CATEGORY_OPTION = [
  { label: 'Investment', value: category2.INVESTMENT },
  { label: 'Effizienz', value: category2.EFFICIENCY },
  { label: 'Andere', value: category2.OTHER },
]

export const PRIORITY_OPTION = [
  { label: <PrioritySelectOption priority={'LOW'} />, value: 'LOW' },
  { label: <PrioritySelectOption priority={'MEDIUM'} />, value: 'MEDIUM' },
  { label: <PrioritySelectOption priority={'HIGH'} />, value: 'HIGH' },
]

const CreateIsoMeasureDtoSchema = yup.object().shape({
  title: yup.string().required(),
  description: yup.string().optional(),
  problem: yup.string().optional(),
  cause: yup.string().optional(),
  determinationDate: yup.string().optional(),
  locationId: yup.number().required(),
  responsiblePerson: yup.string().required(),
  deadline: yup.string().optional(),
  isoGoalId: yup.number().optional(),
  priority: yup.string().oneOf(['LOW', 'MEDIUM', 'HIGH']).required(),
  category: yup.string().oneOf(['INVESTMENT', 'EFFICIENCY', 'OTHER']).optional(),
})

interface CreateMeasureModalProps {
  data?: Partial<CreateIsoMeasureDto>
  insightId?: number
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const CreateMeasureModal = ({ data, insightId, isOpen, onClose, onSuccess }: CreateMeasureModalProps) => {
  const { t } = useTranslation()
  const toast = useBaseToast()
  const session = useSessionStore()
  const isIso50001ModuleEnabled = useIsIso50001ModuleEnabled()
  const userEmail = session.user?.email
  const { locations, currentLocation } = useLocationsContext()
  const { data: users } = useUsers()
  const { data: isoGoals } = useEnergyGoals()

  const getCurrentLocationId = () => {
    // @ts-ignore
    if (!locations && !currentLocation?.locationPgId) {
      return undefined
    }

    if (currentLocation?.locationPgId === -1) {
      return locations[0].rawLocationPgId
    }

    return currentLocation.locationPgId
  }

  const getDefaultCreateMeasure = (): Partial<CreateIsoMeasureDto> => {
    return {
      determinationDate: new Date().toISOString(),
      category: 'EFFICIENCY',
      priority: 'MEDIUM',
      deadline: moment(getEndOfYear()).format('YYYY-MM-DD'),
      locationId: getCurrentLocationId(),
      responsiblePerson: userEmail,
    }
  }

  useEffect(() => {
    if (data) setCreateMeasure(data)
  }, [data])

  useEffect(() => {
    if (!data) setCreateMeasure(getDefaultCreateMeasure())
  }, [currentLocation])

  const [createMeasure, setCreateMeasure] = useState<Partial<CreateIsoMeasureDto>>(data ?? getDefaultCreateMeasure())
  const [isValidForm, setIsValidForm] = useState<boolean>(false)

  useEffect(() => {
    if ((!createMeasure.locationId || createMeasure.locationId === -1) && locations) {
      setCreateMeasure({ ...createMeasure, locationId: locations[0].rawLocationPgId })
    }
  }, [createMeasure.locationId, locations])

  const createMeasureMutation = useCreateMeasure()
  const createFromInsightMutation = useCreateMeasureFromInsight()

  const getGoalOptions = () => {
    if (!isoGoals || isoGoals.goals.length === 0) {
      return []
    }

    return isoGoals.goals
      .filter(goal => goal.parentGoalId !== undefined)
      .map(goal => {
        return { label: goal.title, value: goal.id }
      })
  }

  const getLocationOptions = () => {
    if (!locations) {
      return []
    }
    const locationOptions: { value: number; label: string }[] = []
    for (let i = 0; i < locations.length; i++) {
      if (i === 0) {
        locationOptions.push({ value: locations[i].rawLocationPgId, label: 'Gruppe' })
      }
      if (i > 0) {
        locationOptions.push({ value: locations[i].locationPgId, label: locations[i].name })
      }
    }
    return locationOptions
  }

  const getUserOptions = () => {
    return users.map(user => {
      return { value: user.email, label: `${user.firstName} ${user.lastName}` }
    })
  }

  const handleClose = () => {
    setCreateMeasure(getDefaultCreateMeasure())
    onClose()
  }

  useEffect(() => {
    const validateForm = async () => {
      const isValid = await CreateIsoMeasureDtoSchema.isValid(createMeasure)
      setIsValidForm(isValid)
    }

    void validateForm()
  }, [createMeasure])

  const handleSave = () => {
    if (!isValidForm) {
      return
    }

    const createMeasureDto = { ...createMeasure } as CreateIsoMeasureDto
    if (insightId) {
      createFromInsightMutation.mutate({ insightId, dto: createMeasureDto }, { onSuccess })
    } else {
      createMeasureMutation.mutate(createMeasureDto)
    }

    toast({
      title: t('iso_measures_saved_toast'),
      status: 'success',
      duration: 2000,
      isClosable: false,
    })
    handleClose()
  }

  return (
    <ActionModal
      size={'4xl'}
      header={t('iso_measures_new_measure_modal_title')}
      isOpen={isOpen}
      onSave={handleSave}
      onClose={handleClose}
      isSaveDisabled={!isValidForm}
      cancelButtonDataAction='new-measure-modal-cancel'
      saveButtonDataAction='new-measure-modal-create'
    >
      <FlexRow gap='24px'>
        <FlexCol gap='24px' width={'900px'}>
          <FormInput
            isRequired={true}
            placeholder={t('iso_measures_title_field')}
            onChange={e => {
              setCreateMeasure({ ...createMeasure, title: e.target.value })
            }}
            name='title'
            label={t('iso_measures_title_field')}
            value={createMeasure.title}
          ></FormInput>
          <FormTextarea
            placeholder={t('iso_measures_description_field')}
            height={'56px'}
            onChange={e => {
              setCreateMeasure({ ...createMeasure, description: e.target.value })
            }}
            name='description'
            label={t('iso_measures_description_field')}
            value={createMeasure.description}
          ></FormTextarea>
          <FormTextarea
            placeholder={t('iso_measures_problem_field')}
            height={'56px'}
            onChange={e => {
              setCreateMeasure({ ...createMeasure, problem: e.target.value })
            }}
            name='problem'
            label={t('iso_measures_problem_field')}
            value={createMeasure.problem}
          ></FormTextarea>
          <FormTextarea
            placeholder={t('iso_measures_cause_field')}
            height={'56px'}
            onChange={e => {
              setCreateMeasure({ ...createMeasure, cause: e.target.value })
            }}
            name='cause'
            label={t('iso_measures_cause_field')}
            value={createMeasure.cause}
          ></FormTextarea>
          <FormDatePicker
            pickerType='day'
            name={'determinationDate'}
            label={t('iso_measures_determination_date_field')}
            value={createMeasure.determinationDate ? new Date(createMeasure.determinationDate) : null}
            onChange={date => {
              if (!date) return
              setCreateMeasure({ ...createMeasure, determinationDate: date.toISOString() })
            }}
          ></FormDatePicker>
        </FlexCol>
        <FlexCol paddingX={'16px'} paddingY={'16px'} width={'346px'} gap='16px' borderRadius={'8px'} border='1px solid #E2E8F0'>
          <FormSelect
            isRequired={true}
            name='category-selector'
            label={t('iso_measures_category_field')}
            options={CATEGORY_OPTION}
            value={createMeasure.category}
            onChange={option => {
              setCreateMeasure({ ...createMeasure, category: option })
            }}
          />
          <FormSelect
            isRequired={true}
            name='priority-selector'
            label={t('iso_measures_priority_field')}
            options={PRIORITY_OPTION}
            value={createMeasure.priority}
            onChange={option => {
              setCreateMeasure({ ...createMeasure, priority: option })
            }}
          />
          <FormSelect
            width={'300px'}
            isRequired={true}
            name='location-selector'
            label={t('iso_measures_location_field')}
            value={createMeasure.locationId}
            options={getLocationOptions()}
            onChange={option => {
              setCreateMeasure({ ...createMeasure, locationId: option })
            }}
          />
          <FormSelect
            width={'300px'}
            name='location-selector'
            placeholder={t('iso_select_user')}
            label={t('iso_select_user')}
            options={getUserOptions()}
            value={createMeasure.responsiblePerson}
            onChange={option => {
              setCreateMeasure({ ...createMeasure, responsiblePerson: option })
            }}
          />
          <FormDatePicker
            pickerType='day'
            name={'deadline'}
            label={t('iso_measures_deadline_field')}
            value={createMeasure.deadline ? new Date(createMeasure.deadline) : null}
            onChange={date => {
              if (!date) return
              setCreateMeasure({ ...createMeasure, deadline: moment(date).format('YYYY-MM-DD') })
            }}
          ></FormDatePicker>

          {isIso50001ModuleEnabled && (
            <FormSelect
              width={'300px'}
              name='goal-selector'
              placeholder={t('select_option')}
              label={t('iso_measures_goal_field')}
              value={createMeasure.isoGoalId ?? ''}
              options={[
                ...(createMeasure.isoGoalId ? [{ value: CLEAR_OPTION_VALUE, label: t('deselect_form') }] : []),
                ...(getGoalOptions() ?? []),
              ]}
              onChange={value => {
                setCreateMeasure({
                  ...createMeasure,
                  isoGoalId: value === CLEAR_OPTION_VALUE ? undefined : value,
                })
              }}
            />
          )}
        </FlexCol>
      </FlexRow>
    </ActionModal>
  )
}

export default CreateMeasureModal
